import { EnhancedModel } from '../__generated/configurator-builder.js';
import { implementCustomExpressions } from '../__generated/implementation.js';
import { getReededVariant } from '../data/reeded-data.js';
import { getRogersSingleVariant } from '../data/rogers-data.js';
import { getScaleShapekeyValue, getScaledSizingValue } from './base-double-leg.js';
export const baseSingleLegExpressions = implementCustomExpressions({
    BaseSingleLeg: {
        $offsetY: (s)=>EnhancedModel.$BaseWithPlatform.is(s) ? s.$platformHeight : 0,
        $width: (s)=>{
            if (s.$table.$top.$width < s.$scaleLowerBreakpoint) {
                return s.$scaleLowerBreakpoint * s.$baseWidthMultiplier + s.$activeScaleIncrement;
            }
            return s.$table.$top.$width * s.$baseWidthMultiplier;
        },
        $widthShapekeyValue: (s)=>{
            if (EnhancedModel.$RogersSinglePlatformBase.is(s)) return getRogersSingleVariant(s.$table.$top.$width, EnhancedModel.$TopWithWidthAndLength.is(s.$table.$top)).sizing.width.scaleZero;
            if (s.$table.$top.$width < s.$scaleLowerBreakpoint) {
                return s.$scaleLowerBreakpoint * s.$baseWidthMultiplier;
            }
            return s.$table.$top.$width * s.$baseWidthMultiplier - s.$activeScaleIncrement;
        },
        $widthScaleZero: (s)=>{
            if (EnhancedModel.$ReededBase.is(s)) return getReededVariant(s.$table.$top.$width).sizing.scaleZero;
            if (EnhancedModel.$RogersSinglePlatformBase.is(s)) return getRogersSingleVariant(s.$table.$top.$width, EnhancedModel.$TopWithWidthAndLength.is(s.$table.$top)).sizing.width.scaleZero;
            return s.$baseDataMap[s.type]?.sizing.width.scaleZero;
        },
        $widthScaleOne: (s)=>{
            if (EnhancedModel.$ReededBase.is(s)) return getReededVariant(s.$table.$top.$width).sizing.scaleOne;
            if (EnhancedModel.$RogersSinglePlatformBase.is(s)) return getRogersSingleVariant(s.$table.$top.$width, EnhancedModel.$TopWithWidthAndLength.is(s.$table.$top)).sizing.width.scaleOne;
            return s.$baseDataMap[s.type]?.sizing.width.scaleOne;
        },
        $length: (s)=>{
            let length;
            if (!EnhancedModel.$TopWithWidthAndLength.is(s.$table.$top) && s.$table.$top.$width < s.$scaleLowerBreakpoint) {
                length = s.$scaleLowerBreakpoint * s.$baseLengthMultiplier + s.$activeScaleIncrement;
            } else {
                length = s.$table.$top.$length * s.$baseLengthMultiplier;
            }
            if (EnhancedModel.$PeruStretchedBase.is(s) || EnhancedModel.$BellyStretchedBase.is(s)) {
                const sizing = s.$baseDataMap[s.type]?.sizing;
                const lengthScaleZero = 'length' in sizing ? sizing.length.scaleZero : 0;
                return Math.max(length, lengthScaleZero);
            }
            return length;
        },
        $lengthShapekeyValue: (s)=>{
            if (EnhancedModel.$RogersSinglePlatformBase.is(s)) return getRogersSingleVariant(s.$table.$top.$width, EnhancedModel.$TopWithWidthAndLength.is(s.$table.$top)).sizing.length.scaleZero;
            let value;
            if (!EnhancedModel.$TopWithWidthAndLength.is(s.$table.$top) && s.$table.$top.$width < s.$scaleLowerBreakpoint) {
                value = s.$scaleLowerBreakpoint * s.$baseLengthMultiplier;
            } else {
                value = s.$table.$top.$length * s.$baseLengthMultiplier - s.$activeScaleIncrement;
            }
            if (EnhancedModel.$PeruStretchedBase.is(s) || EnhancedModel.$BellyStretchedBase.is(s)) {
                const sizing = s.$baseDataMap[s.type]?.sizing;
                const lengthScaleZero = 'length' in sizing ? sizing.length.scaleZero : 0;
                return Math.max(value, lengthScaleZero);
            }
            return value;
        },
        $lengthScaleZero: (s)=>{
            if (EnhancedModel.$ReededBase.is(s)) return getReededVariant(s.$table.$top.$width).sizing.scaleZero;
            if (EnhancedModel.$RogersSinglePlatformBase.is(s)) return getRogersSingleVariant(s.$table.$top.$width, EnhancedModel.$TopWithWidthAndLength.is(s.$table.$top)).sizing.length.scaleZero;
            const sizing = s.$baseDataMap[s.type]?.sizing;
            return 'length' in sizing ? sizing.length.scaleZero : 0;
        },
        $lengthScaleOne: (s)=>{
            if (EnhancedModel.$ReededBase.is(s)) return getReededVariant(s.$table.$top.$width).sizing.scaleOne;
            if (EnhancedModel.$RogersSinglePlatformBase.is(s)) return getRogersSingleVariant(s.$table.$top.$width, EnhancedModel.$TopWithWidthAndLength.is(s.$table.$top)).sizing.length.scaleOne;
            const sizing = s.$baseDataMap[s.type]?.sizing;
            return 'length' in sizing ? sizing.length.scaleOne : 0;
        },
        $activeScaleIncrement: (s)=>{
            if (s.$table.$top.$width > s.$scaleUpperBreakpoint) {
                return s.$breakpointScaleIncrement;
            }
            if (s.$table.$top.$width < s.$scaleLowerBreakpoint) {
                return -s.$breakpointScaleIncrement;
            }
            return 0;
        },
        $scaleShapekeyValue: (s)=>{
            if (EnhancedModel.$RogersSinglePlatformBase.is(s)) return getRogersSingleVariant(s.$table.$top.$width, EnhancedModel.$TopWithWidthAndLength.is(s.$table.$top)).sizing.scale.scaleZero;
            const increment = s.$baseDataMap[s.type].sizing.scale.increment;
            if (!increment || increment <= 0) {
                return getScaleShapekeyValue(s.$widthShapekeyValue, s.$widthScaleZero, s.$widthScaleOne, s.$scaleScaleZero, s.$scaleScaleOne, 0, s.$activeScaleIncrement);
            }
            return s.$scaleScaleZero + s.$activeScaleIncrement;
        },
        $scaleScaleZero: (s)=>{
            if (EnhancedModel.$RogersSinglePlatformBase.is(s)) return getRogersSingleVariant(s.$table.$top.$width, EnhancedModel.$TopWithWidthAndLength.is(s.$table.$top)).sizing.scale.scaleZero;
            return s.$baseDataMap[s.type].sizing.scale.scaleZero;
        },
        $scaleScaleOne: (s)=>{
            if (EnhancedModel.$RogersSinglePlatformBase.is(s)) return getRogersSingleVariant(s.$table.$top.$width, EnhancedModel.$TopWithWidthAndLength.is(s.$table.$top)).sizing.scale.scaleOne;
            return s.$baseDataMap[s.type].sizing.scale.scaleOne;
        },
        $baseWidthMultiplier: (s)=>{
            let defaultWidth = s.$defaultTopWidthForSingleLeg;
            if (EnhancedModel.$StretchedBase.is(s)) {
                defaultWidth = s.$table.$top.$defaultWidth;
            }
            return s.$baseDataMap[s.type].sizing.width.scaleZero / defaultWidth;
        },
        $baseLengthMultiplier: (s)=>{
            let defaultLength = s.$defaultTopLengthForSingleLeg;
            if (EnhancedModel.$StretchedBase.is(s)) {
                defaultLength = s.$table.$top.$defaultLength;
            }
            const sizing = s.$baseDataMap[s.type].sizing;
            return 'length' in sizing ? sizing.length.scaleZero / defaultLength : 0;
        },
        // Platform
        $platformFilter: (s)=>EnhancedModel.$BaseWithPlatform.is(s),
        $platformObjectName: (s)=>s.$platformDataMap[s.$table.$top.type]?.objectName,
        $platformWidth: (s)=>s.$table.$top.$width * s.$platformToTopRatio,
        $platformWidthScaleZero: (s)=>s.$platformDataMap[s.$table.$top.type]?.sizing.width.scaleZero,
        $platformWidthScaleOne: (s)=>s.$platformDataMap[s.$table.$top.type]?.sizing.width.scaleOne,
        $platformLength: (s)=>s.$table.$top.$length * s.$platformToTopRatio,
        $platformLengthScaleZero: (s)=>s.$platformDataMap[s.$table.$top.type]?.sizing.length.scaleZero,
        $platformLengthScaleOne: (s)=>s.$platformDataMap[s.$table.$top.type]?.sizing.length.scaleOne,
        $platformRepeatX: (s)=>s.$platformWidth / s.$table.$woodTextureSize,
        $platformRepeatY: (s)=>s.$platformLength / s.$table.$woodTextureSize,
        $widthIndicatorFixedY: (s)=>s.$offsetY,
        $widthIndicatorFixedZ: ()=>0,
        $widthIndicatorExtrusionZ: (s)=>s.$table.$top.$length / 2,
        $feetThickness: (s)=>{
            if (EnhancedModel.$MarksBase.is(s)) return s.$lengthShapekeyValue;
            const sizing = EnhancedModel.$ReededBase.is(s) ? getReededVariant(s.$table.$top.$width).sizing : s.$baseDataMap[s.type].sizing;
            return getScaledSizingValue(sizing, 'feetThickness', 'feetThicknessScaleOne', s.$scaleShapekeyValue, s.$scaleScaleZero, s.$scaleScaleOne);
        },
        $feetThicknessIndicatorFixedX: (s)=>s.$width / 2,
        $feetThicknessIndicatorStartZ: (s)=>-s.$feetThickness / 2,
        $feetThicknessIndicatorEndZ: (s)=>s.$feetThickness / 2,
        $feetThicknessIndicatorVisible: (s)=>!EnhancedModel.$BaseWithPlatform.is(s) && !EnhancedModel.$ReededBase.is(s) && !EnhancedModel.$ReflectionCTCBase.is(s),
        $baseThickness: (s)=>{
            if (EnhancedModel.$PeruStretchedBase.is(s)) return s.$lengthShapekeyValue;
            if (EnhancedModel.$BellyStretchedBase.is(s)) return s.$lengthShapekeyValue - 1.75 * 2 // 1.75 is the static diff between full length and belly length
            ;
            const sizing = EnhancedModel.$ReededBase.is(s) ? getReededVariant(s.$table.$top.$width).sizing : EnhancedModel.$RogersSinglePlatformBase.is(s) ? getRogersSingleVariant(s.$table.$top.$width, EnhancedModel.$TopWithWidthAndLength.is(s.$table.$top)).sizing : s.$baseDataMap[s.type].sizing;
            return getScaledSizingValue(sizing, 'baseThickness', 'baseThicknessScaleOne', s.$scaleShapekeyValue, s.$scaleScaleZero, s.$scaleScaleOne);
        },
        $baseThicknessIndicatorFixedY: (s)=>{
            const sizing = EnhancedModel.$ReededBase.is(s) ? getReededVariant(s.$table.$top.$width).sizing : EnhancedModel.$RogersSinglePlatformBase.is(s) ? getRogersSingleVariant(s.$table.$top.$width, EnhancedModel.$TopWithWidthAndLength.is(s.$table.$top)).sizing : s.$baseDataMap[s.type].sizing;
            return getScaledSizingValue(sizing, 'baseThicknessCenterY', 'baseThicknessCenterYScaleOne', s.$scaleShapekeyValue, s.$scaleScaleZero, s.$scaleScaleOne);
        },
        $baseThicknessIndicatorVisible: (s)=>s.$baseThickness > 0 && !EnhancedModel.$ReededBase.is(s) && !EnhancedModel.$ReflectionCTCBase.is(s),
        $baseThicknessIndicatorStartZ: (s)=>-s.$baseThickness / 2,
        $baseThicknessIndicatorEndZ: (s)=>s.$baseThickness / 2,
        $baseThicknessIndicatorExtrusionX: (s)=>s.$width / 2 + 12,
        $platformWidthIndicatorVisible: (s)=>EnhancedModel.$BaseWithPlatform.is(s),
        $platformWidthIndicatorStartX: (s)=>-s.$platformWidth / 2,
        $platformWidthIndicatorEndX: (s)=>s.$platformWidth / 2,
        $platformWidthIndicatorExtrusionZ: (s)=>s.$table.$top.$length / 2,
        $platformLengthIndicatorVisible: (s)=>EnhancedModel.$BaseWithPlatform.is(s),
        $platformLengthIndicatorStartZ: (s)=>-s.$platformLength / 2,
        $platformLengthIndicatorEndZ: (s)=>s.$platformLength / 2,
        $platformLengthIndicatorExtrusionX: (s)=>s.$table.$top.$width / 2
    }
});

//# sourceMappingURL=base-single-leg.js.map